import { forwardRef, startTransition, useEffect, useId, useRef, useState, type CSSProperties, type InputHTMLAttributes } from "react";
import { cn } from "@/shared/lib/cn";
import { useSliderSwipe } from "./useSliderSwipe";

export interface SliderProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type" | "size" | "value" | "defaultValue" | "min" | "max" | "onChange"> {
  label: string;
  value: number;
  onValueChange: (value: number) => void;
  min?: number;
  max?: number;
  description?: string;
  error?: string;
  formatValue?: (value: number) => string;
  hideLabel?: boolean;
  showValue?: boolean;
  tone?: "accent" | "image" | "video" | "audio" | "neutral";
}

/** Filled track without a visible handle; native touch/keyboard and horizontal trackpad adjustment. */
export const Slider = forwardRef<HTMLInputElement, SliderProps>(function Slider({
  label, value, onValueChange, min = 0, max = 100, step = 1,
  description, error, formatValue = String, hideLabel = false, showValue = true,
  tone = "neutral", id: providedId, className, style,
  "aria-describedby": describedBy, "aria-invalid": invalid, ...props
}, ref) {
  const generatedId = useId();
  const [dragValue, setDragValue] = useState<number | null>(null);
  const dragging = useRef(false);
  const frame = useRef<number | null>(null);
  const pendingValue = useRef<number | null>(null);
  const change = useRef(onValueChange);
  change.current = onValueChange;
  const finish = useRef<() => void>(() => {});
  finish.current = () => {
    if (!dragging.current) return;
    dragging.current = false;
    if (frame.current !== null) cancelAnimationFrame(frame.current);
    frame.current = null;
    if (pendingValue.current !== null) change.current(pendingValue.current);
    pendingValue.current = null;
    setDragValue(null);
  };
  useEffect(() => {
    const end = () => finish.current();
    window.addEventListener("pointerup", end);
    window.addEventListener("pointercancel", end);
    return () => {
      window.removeEventListener("pointerup", end);
      window.removeEventListener("pointercancel", end);
      if (frame.current !== null) cancelAnimationFrame(frame.current);
    };
  }, []);
  useEffect(() => {
    if (!props.disabled) return;
    dragging.current = false;
    pendingValue.current = null;
    if (frame.current !== null) cancelAnimationFrame(frame.current);
    frame.current = null;
    setDragValue(null);
  }, [props.disabled]);
  const id = providedId ?? generatedId;
  const lower = Number.isFinite(min) ? min : 0;
  const upper = Number.isFinite(max) ? Math.max(lower, max) : Math.max(lower, 100);
  const displayedValue = dragValue ?? value;
  const bounded = Math.min(upper, Math.max(lower, Number.isFinite(displayedValue) ? displayedValue : lower));
  const numericStep = Number(step);
  const increment = Number.isFinite(numericStep) && numericStep > 0 ? numericStep : 1;
  // Keep the visible/spoken value aligned with native range step sanitization.
  const stepIndex = Math.min(Math.floor((upper - lower) / increment + 1e-9), Math.round((bounded - lower) / increment));
  const current = step === "any" ? bounded : Number((lower + stepIndex * increment).toPrecision(12));
  const control = useSliderSwipe({
    value: current, min: lower, max: upper, step: step === "any" ? "any" : increment,
    disabled: props.disabled || props.readOnly || dragValue !== null, onValueChange,
  });
  const percentage = upper === lower ? 0 : (current - lower) / (upper - lower) * 100;
  const descriptionId = description ? `${id}-description` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  return (
    <div className={cn("studio-slider", className)} data-tone={tone} data-disabled={props.disabled || undefined}>
      <div className={cn("studio-slider-heading", hideLabel && !showValue && "sr-only")}>
        <label htmlFor={id} className={hideLabel ? "sr-only" : undefined}>{label}</label>
        {showValue ? <output htmlFor={id} aria-hidden="true">{formatValue(current)}</output> : null}
      </div>
      <div ref={control} className="studio-slider-control" data-dragging={dragValue !== null || undefined}
        style={{ "--slider-progress": `${percentage}%`, "--slider-fraction": percentage / 100 } as CSSProperties}>
        <span className="studio-slider-rail" aria-hidden="true"><span /></span>
        <input
        {...props}
        ref={ref}
        id={id}
        type="range"
        min={lower}
        max={upper}
        step={step === "any" ? "any" : increment}
        value={current}
        aria-valuetext={props["aria-valuetext"] ?? formatValue(current)}
        aria-invalid={error ? true : invalid}
        aria-describedby={[describedBy, descriptionId, errorId].filter(Boolean).join(" ") || undefined}
        onPointerDown={(event) => {
          props.onPointerDown?.(event);
          if (props.disabled || event.defaultPrevented || event.button !== 0) return;
          dragging.current = true;
          setDragValue(current);
        }}
        onChange={(event) => {
          const next = event.currentTarget.valueAsNumber;
          if (!dragging.current) { onValueChange(next); return; }
          // The thumb updates locally at input speed. Expensive preview updates
          // are coalesced to a frame and run at a lower React priority.
          setDragValue(next);
          pendingValue.current = next;
          if (frame.current !== null) return;
          frame.current = requestAnimationFrame(() => {
            frame.current = null;
            const latest = pendingValue.current;
            if (latest !== null) startTransition(() => change.current(latest));
          });
        }}
        className="studio-slider-input"
        style={{ ...style, "--slider-progress": `${percentage}%` } as CSSProperties}
      />
      </div>
      {description ? <p id={descriptionId} className="studio-control-description">{description}</p> : null}
      {error ? <p id={errorId} role="status" className="studio-control-error">{error}</p> : null}
    </div>
  );
});
